import { useLightswitch } from './useLightswitch.js';

/**
 * Lightswitch Customer Portal Hook
 * 
 * Custom hook that provides customer portal functions for managing subscriptions.
 * This hook provides functions that can be directly attached to button onClick handlers.
 */

/**
 * Hook for Lightswitch customer portal actions
 * @returns {Object} Customer portal functions and state
 */
const useCustomerPortal = () => {
  const {
    isAuthenticated,
    isLoading,
    user,
    isInitialized,
    isReady,
    core
  } = useLightswitch();

  /**
   * Handle customer portal button click
   * Creates a portal session and redirects to Stripe's customer portal
   * @param {string} [returnUrl] - URL to redirect back to after portal session (optional, defaults to current page)
   */
  const handleCustomerPortal = async (returnUrl) => {
    if (!isInitialized || !isReady) {
      console.warn('Lightswitch SDK not ready yet');
      return;
    }

    if (!isAuthenticated) {
      console.warn('User must be authenticated to access customer portal');
      return;
    }
    
    try {
      // Convert path to full URL if provided and it's a relative path
      let finalReturnUrl = returnUrl;
      if (returnUrl && !returnUrl.startsWith('http')) {
        finalReturnUrl = `${window.location.origin}${returnUrl}`;
      }
      
      await core.redirectToCustomerPortal(finalReturnUrl);
    } catch (error) {
      console.error('Customer portal error:', error);
      throw error;
    }
  };

  /**
   * Create customer portal URL without redirecting
   * Useful for custom handling or opening in new tab
   * @param {string} [returnUrl] - URL to redirect back to after portal session (optional, defaults to current page)
   * @returns {Promise<string>} The portal URL
   */
  const createPortalUrl = async (returnUrl) => {
    if (!isInitialized || !isReady) {
      throw new Error('Lightswitch SDK not ready yet');
    }

    if (!isAuthenticated) {
      throw new Error('User must be authenticated to access customer portal');
    }
    
    try {
      // Convert path to full URL if provided and it's a relative path
      let finalReturnUrl = returnUrl;
      if (returnUrl && !returnUrl.startsWith('http')) {
        finalReturnUrl = `${window.location.origin}${returnUrl}`;
      }
      
      return await core.createCustomerPortal(finalReturnUrl);
    } catch (error) {
      console.error('Failed to create customer portal URL:', error);
      throw error;
    }
  };

  return {
    // State
    isAuthenticated,
    isLoading,
    user,
    isReady: isReady && isInitialized,
    
    // Action handlers
    handleCustomerPortal,
    createPortalUrl,
    
    // Convenience function for checking if portal is available
    canAccessPortal: isReady && isInitialized && isAuthenticated
  };
};

export { useCustomerPortal };
